import type { CompanyMeView, CompanySessionTokens, MeView, SessionTokens } from '@lp/contracts';
import { create } from 'zustand';

const KEY = 'lp-session';

interface Session {
  accessToken: string;
  refreshToken: string;
  /** a platform user, or (Phase 9) a company user — told apart by `kind`: 'company' */
  user: MeView | CompanyMeView;
}

function load(): Session | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function save(s: Session | null): void {
  try {
    if (s) window.localStorage.setItem(KEY, JSON.stringify(s));
    else window.localStorage.removeItem(KEY);
  } catch {
    /* private window: the session lasts for this page only */
  }
}

interface AuthState {
  session: Session | null;
  signedIn: (t: SessionTokens | CompanySessionTokens) => void;
  setUser: (u: MeView | CompanyMeView) => void;
  clear: () => void;
}

/** the signed-in platform user and the tokens; kept in this browser so a reload stays signed in */
export const useAuth = create<AuthState>((set, get) => ({
  session: load(),
  signedIn: (t) => {
    const s = { accessToken: t.accessToken, refreshToken: t.refreshToken, user: t.user };
    save(s);
    set({ session: s });
  },
  setUser: (user) => {
    const cur = get().session;
    if (!cur) return;
    const s = { ...cur, user };
    save(s);
    set({ session: s });
  },
  clear: () => {
    save(null);
    set({ session: null });
  },
}));

/** only the Super Admin changes the catalog and the clients; platform staff read */
export const useIsSuperAdmin = (): boolean => useAuth((s) => s.session?.user.role === 'super_admin');

/** a company user's session (its own console) */
export const isCompany = (u: MeView | CompanyMeView | undefined): u is CompanyMeView => !!u && 'kind' in u && u.kind === 'company';
export const useCompanyUser = (): CompanyMeView | null => useAuth((s) => (isCompany(s.session?.user) ? (s.session!.user as CompanyMeView) : null));
